import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { API } from "../App";
import { Ship, ArrowRight, Loader2 } from "lucide-react";
import { Button } from "../components/ui/button";
import { renderMarkdownSafe } from "../lib/safeMarkdown";
import { useJsonLd, TAXINEARME_SITE_URL } from "../lib/seo";
import SiteNav from "../components/SiteNav";

const HERO_FALLBACK =
  "https://images.unsplash.com/photo-1516483638261-f4dbaf036963?auto=format&fit=crop&q=80&w=2000";

export default function VeniceHotelsPage() {
  const [hotels, setHotels] = useState([]);
  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/venice-hotels`),
      axios.get(`${API}/venice-hotels-page`),
    ])
      .then(([h, p]) => {
        setHotels(h.data || []);
        setPage(p.data || {});
      })
      .catch((err) => {
        console.warn("Failed to load Venice hotels page", err);
        toast.error("Could not load hotels list");
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!page) return;
    if (page.meta_title) document.title = page.meta_title;
    return () => { document.title = "TaxiNearMe"; };
  }, [page]);

  useJsonLd(
    [
      {
        id: "tnm-hotels-breadcrumbs",
        data: {
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: `${TAXINEARME_SITE_URL}/` },
            { "@type": "ListItem", position: 2, name: "Venice Hotel Transfers", item: `${TAXINEARME_SITE_URL}/hotels-in-venice` },
          ],
        },
      },
      hotels.length > 0
        ? {
            id: "tnm-hotels-itemlist",
            data: {
              "@context": "https://schema.org",
              "@type": "ItemList",
              name: "Top Venice hotels — TaxiNearMe",
              numberOfItems: hotels.length,
              itemListElement: hotels.map((h, idx) => ({
                "@type": "ListItem",
                position: idx + 1,
                name: h.name,
                url: `${TAXINEARME_SITE_URL}/hotels-in-venice/${h.slug}`,
              })),
            },
          }
        : null,
    ].filter(Boolean)
  );

  const heroImage = page?.hero_image_url || HERO_FALLBACK;

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white" data-testid="venice-hotels-page">
      <SiteNav />
      {/* Hero */}
      <section
        className="relative h-[65vh] min-h-[420px] flex flex-col justify-end px-4 sm:px-8 lg:px-16 pt-24 pb-10"
        style={{
          backgroundImage: `linear-gradient(180deg, rgba(0,0,0,0.25) 0%, rgba(0,0,0,0.55) 55%, rgba(10,10,10,1) 100%), url(${heroImage})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="max-w-6xl mx-auto w-full">
          <div className="inline-flex items-center gap-2 text-[#C5A059] text-xs uppercase tracking-widest mb-3">
            <Ship className="w-4 h-4" /> Airport meet &amp; greet · Water taxi included
          </div>
          <h1
            className="text-3xl sm:text-4xl lg:text-5xl font-serif text-white leading-tight drop-shadow-[0_2px_10px_rgba(0,0,0,0.7)]"
            data-testid="hotels-page-title"
          >
            {page?.hero_title || "Luxury Hotel Transfers in Venice"}
          </h1>
          <p className="text-white/70 text-lg mt-3 max-w-2xl drop-shadow-[0_2px_10px_rgba(0,0,0,0.6)]">
            {page?.hero_subtitle || "Meet & greet at Venice Marco Polo Airport, water taxi included."}
          </p>
        </div>
      </section>

      {/* Intro description (editable HTML) */}
      {page?.intro_html && (
        <section className="max-w-4xl mx-auto px-4 sm:px-8 py-10">
          <div
            className="prose-custom text-white/80 leading-relaxed"
            dangerouslySetInnerHTML={renderMarkdownSafe(page.intro_html)}
          />
        </section>
      )}

      {/* Hotel grid */}
      <section className="max-w-6xl mx-auto px-4 sm:px-8 py-12">
        <div className="flex items-baseline justify-between mb-8">
          <h2 className="text-2xl sm:text-3xl font-serif text-white">
            Top hotels in Venice <span className="text-white/40 text-lg">({hotels.length})</span>
          </h2>
          <span className="text-[#C5A059] text-xs uppercase tracking-widest">Fixed all-in price</span>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-24 text-white/50 gap-2">
            <Loader2 className="w-5 h-5 animate-spin" /> Loading hotels...
          </div>
        ) : hotels.length === 0 ? (
          <p className="text-white/50 text-center py-24">No hotels published yet.</p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {hotels.map((h) => (
              <Link
                key={h.hotel_id}
                to={`/hotels-in-venice/${h.slug}`}
                className="group bg-[#111] border border-white/5 rounded-sm overflow-hidden transition-all duration-300 hover:border-[#C5A059]/60 hover:-translate-y-1"
                data-testid={`hotel-card-${h.slug}`}
              >
                <div
                  className="h-40 bg-cover bg-center"
                  style={{
                    backgroundImage: `linear-gradient(180deg, rgba(0,0,0,0) 55%, rgba(17,17,17,1) 100%), url(${h.hero_image_url || HERO_FALLBACK})`,
                  }}
                />
                <div className="p-5">
                  <div className="text-[10px] uppercase tracking-widest text-[#C5A059] mb-1">Venice</div>
                  <h3 className="text-white text-lg font-medium leading-tight mb-2 group-hover:text-[#C5A059] transition-colors">
                    {h.name}
                  </h3>
                  {h.short_description && (
                    <p className="text-white/50 text-sm line-clamp-2 mb-4">{h.short_description}</p>
                  )}
                  <div className="flex items-center justify-between pt-2 border-t border-white/5">
                    <div>
                      <div className="text-[10px] text-white/40 uppercase">Airport transfer</div>
                      <div className="text-[#C5A059] text-lg font-mono">
                        €{(h.airport_price || 0).toFixed(0)}
                      </div>
                    </div>
                    <span className="text-white/40 group-hover:text-[#C5A059] transition-colors">
                      <ArrowRight className="w-5 h-5" />
                    </span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </section>

      {/* Bottom CTA — "See all routes" */}
      <section className="max-w-6xl mx-auto px-4 sm:px-8 pb-20">
        <div className="border border-[#C5A059]/40 rounded-sm p-8 sm:p-12 text-center bg-gradient-to-b from-[#111] to-[#0A0A0A]">
          <h3 className="text-2xl sm:text-3xl font-serif text-white mb-2">
            Traveling somewhere else?
          </h3>
          <p className="text-white/60 mb-6 max-w-2xl mx-auto">
            Beyond Venice hotels we serve 99+ curated routes to the Dolomites, cruise terminals, cities and beyond.
          </p>
          <Link to="/routes">
            <Button
              className="btn-gold text-base px-10 py-6 uppercase tracking-widest"
              data-testid="see-all-routes-btn"
            >
              See all routes <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
}
